import React, { useEffect, useRef } from 'react';

interface GameOfLifeBackgroundProps {
  statusCode?: number;
}

const GameOfLifeBackground: React.FC<GameOfLifeBackgroundProps> = ({ statusCode }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrame = 0;
    let running = true;

    const cellSize = 8; // small squares

    let cols = 0;
    let rows = 0;
    let grid: number[][] = [];
    let cellAge: number[][] = []; // track cell age for smooth brightness transition
    let cellDeath: number[][] = []; // track death progression for fade-out

    const resize = () => {
      // full screen canvas
      const dpr = Math.max(1, window.devicePixelRatio || 1);
      const fullW = window.innerWidth * dpr;
      const fullH = window.innerHeight * dpr;
      canvas.width = Math.floor(fullW);
      canvas.height = Math.floor(fullH);
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      cols = Math.floor(canvas.width / dpr / cellSize);
      rows = Math.floor(canvas.height / dpr / cellSize);

      // initialize grid sized to rows x cols
      grid = Array.from({ length: rows }, () => Array(cols).fill(0));
      cellAge = Array.from({ length: rows }, () => Array(cols).fill(0)); // track age
      cellDeath = Array.from({ length: rows }, () => Array(cols).fill(0)); // track death

      // seed only bottom-right area (from center outward to edges)
      const seedStartCol = Math.floor(cols * 0.5);
      const seedStartRow = Math.floor(rows * 0.5);

      for (let r = seedStartRow; r < rows; r++) {
        for (let c = seedStartCol; c < cols; c++) {
          if (Math.random() > 0.85) {
            grid[r][c] = 1;
            cellAge[r][c] = 1; // start age at 1 for fade-in
            cellDeath[r][c] = 0;
          }
        }
      }
    };

    const countNeighbors = (r: number, c: number) => {
      let count = 0;
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          if (dr === 0 && dc === 0) continue;
          const nr = r + dr;
          const nc = c + dc;
          if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) {
            count += grid[nr][nc];
          }
        }
      }
      return count;
    };

    const updateGrid = () => {
      const next = grid.map((row) => row.slice());
      const nextAge = cellAge.map((row) => row.slice());
      const nextDeath = cellDeath.map((row) => row.slice());

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const n = countNeighbors(r, c);
          if (grid[r][c] === 1) {
            if (n < 2 || n > 3) {
              // Cell dies - start death fade-out
              next[r][c] = 0;
              nextDeath[r][c] = 1; // start death progression
              nextAge[r][c] = 0;
            } else {
              // Cell survives
              nextAge[r][c] = Math.min(255, cellAge[r][c] + 1); // age up
              nextDeath[r][c] = 0; // reset death
            }
          } else {
            if (n === 3) {
              // Cell born
              next[r][c] = 1;
              nextAge[r][c] = 1; // new cell starts at age 1
              nextDeath[r][c] = 0;
            } else {
              // Continue death fade-out if in progress
              if (cellDeath[r][c] > 0) {
                nextDeath[r][c] = Math.min(5, cellDeath[r][c] + 1); // fade out over 5 updates
              }
            }
          }
        }
      }
      grid = next;
      cellAge = nextAge;
      cellDeath = nextDeath;
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const canvasW = canvas.width / (window.devicePixelRatio || 1);
      const canvasH = canvas.height / (window.devicePixelRatio || 1);
      const centerX = canvasW * 0.5;
      const centerY = canvasH * 0.5;

      // Determine base color based on status code
      let baseColor = '#888888'; // gray for main page
      if (statusCode) {
        if (statusCode >= 500) baseColor = '#ff6b6b'; // red for 5xx
        else if (statusCode >= 400) baseColor = '#ffa500'; // orange for 4xx
        else if (statusCode >= 300) baseColor = '#ffd700'; // gold for 3xx
        else if (statusCode >= 200) baseColor = '#4ecca3'; // green for 2xx
      }

      // Parse hex color to RGB for brightness variations
      const hexToRgb = (hex: string) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        } : { r: 136, g: 136, b: 136 };
      };

      const rgb = hexToRgb(baseColor);
      const colors = [
        baseColor, // brightest
        `rgb(${Math.floor(rgb.r * 0.65)}, ${Math.floor(rgb.g * 0.65)}, ${Math.floor(rgb.b * 0.65)})`, // medium
        `rgb(${Math.floor(rgb.r * 0.35)}, ${Math.floor(rgb.g * 0.35)}, ${Math.floor(rgb.b * 0.35)})`, // dimmest
      ];

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const x = c * cellSize;
          const y = r * cellSize;

          // distance from center to edges in bottom-right quadrant
          const distFromCenterX = Math.max(0, x - centerX);
          const distFromCenterY = Math.max(0, y - centerY);
          const maxDistX = canvasW - centerX;
          const maxDistY = canvasH - centerY;
          
          const fadeX = Math.min(1, distFromCenterX / maxDistX);
          const fadeY = Math.min(1, distFromCenterY / maxDistY);
          const fade = Math.min(fadeX, fadeY);

          if (fade < 0.05) continue;

          // Determine which state: alive, dying, or dead with fade-out
          let lifeAlpha = 0;
          if (grid[r][c] === 1) {
            // Alive cell: fade in over 15 updates
            lifeAlpha = Math.min(1, cellAge[r][c] / 15);
          } else if (cellDeath[r][c] > 0) {
            // Dying cell: fade out over 5 updates (faster)
            lifeAlpha = Math.max(0, 1 - cellDeath[r][c] / 5);
          } else {
            continue; // not alive or dying, skip
          }

          // pick brightness level deterministically per cell
          const cellBright = ((r + c) * 7) % 3;
          const color = colors[cellBright];
          const baseAlpha = [0.95, 0.70, 0.45][cellBright]; // increased visibility

          const finalAlpha = baseAlpha * fade * lifeAlpha;
          ctx.fillStyle = color;
          ctx.globalAlpha = finalAlpha;
          ctx.fillRect(x + 1, y + 1, Math.max(1, cellSize - 2), Math.max(1, cellSize - 2));
        }
      }
      ctx.globalAlpha = 1;
    };

    const updateInterval = 12; // slow animation for professional look
    let frame = 0;

    const loop = () => {
      if (!running) return;
      frame++;
      
      if (frame % updateInterval === 0) {
        updateGrid();
      }
      draw();
      animationFrame = requestAnimationFrame(loop);
    };

    resize();
    window.addEventListener('resize', resize);
    loop();

    return () => {
      running = false;
      window.removeEventListener('resize', resize);
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [statusCode]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
};

export default GameOfLifeBackground;
